import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { SYSTEMS_CATALOG, type SystemDefinition } from '../types/systems';

export interface SystemProgress {
  definition:  SystemDefinition;
  usageCount:  number;
  lastUsed:    string | null;
  isExplored:  boolean;
}

export interface UserJourney {
  systems:       SystemProgress[];
  exploredCount: number;
  totalCount:    number;
  percentage:    number;
  recommended:   SystemDefinition[];
  loading:       boolean;
}

export function useUserJourney(): UserJourney {
  const [systems,  setSystems]  = useState<SystemProgress[]>([]);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || cancelled) { setLoading(false); return; }

      const { data } = await supabase
        .from('system_usage')
        .select('system_name, usage_count, last_used')
        .eq('user_id', user.id);

      if (cancelled) return;

      const usageMap = new Map<string, { count: number; lastUsed: string }>(
        (data ?? []).map(r => [r.system_name, { count: r.usage_count, lastUsed: r.last_used }])
      );

      const progress: SystemProgress[] = SYSTEMS_CATALOG.map(def => {
        const u = usageMap.get(def.key);
        return {
          definition:  def,
          usageCount:  u?.count   ?? 0,
          lastUsed:    u?.lastUsed ?? null,
          isExplored:  (u?.count ?? 0) > 0,
        };
      });

      setSystems(progress);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  const exploredCount = systems.filter(s => s.isExplored).length;
  const totalCount    = SYSTEMS_CATALOG.length;

  // Recommend unexplored systems that aren't premium, sorted by category variety
  const unexplored = systems.filter(s => !s.isExplored && !s.definition.isPremium);
  const recommended = unexplored.slice(0, 3).map(s => s.definition);

  return {
    systems,
    exploredCount,
    totalCount,
    percentage: totalCount > 0 ? Math.round((exploredCount / totalCount) * 100) : 0,
    recommended,
    loading,
  };
}
